import axios, { AxiosInstance, AxiosResponse } from 'axios';
import {
  ApiResponse,
  PublishedPaginatedResponse,
  User,
  Category,
  Mentor,
  CourseOwner,
  Course,
  Lesson,
  Purchase,
  PayMethodDetail,
  Unlocks,
  Statistics,
  PurchaseFormData,
  LoginCredentials,
  LessonProgress,
  CourseProgress,
  ContactInfo,
  Profile,
  Exam,
  Question,
  ExamAttempt,
  UserAnswer,
  StandaloneExam,
  StandaloneExamAttempt,
  StandaloneQuestion,
  StandaloneExamResultSummary,
  StandaloneExamPurchase,
  StandaloneExamPurchaseFormData,
  CertificateVerifyResult,
} from '../types';

class ApiService {
  private api: AxiosInstance;

  constructor() {
    this.api = axios.create({
      baseURL: process.env.REACT_APP_API_URL || 'http://localhost:8000/api/v1',
      timeout: 10000,
      headers: {
        'Content-Type': 'application/json',
      },
    });

    this.api.interceptors.request.use(
      (config) => {
        const isGet = (config.method || 'get').toLowerCase() === 'get';
        const url = config.url || '';
        const isPublicGet = isGet && (
          url.includes('/mentors/published/') ||
          url.includes('/courses/published/') ||
          url.includes('/courses/featured/') ||
          url.includes('/statistics/public/') ||
          url.includes('/certificates/verify/') ||
          // List-only: allow unauthenticated GET so exam list is public. Detail (e.g. /standalone-exams/abc123/) needs auth so backend can return user_has_access.
          (url.includes('standalone-exams') && !url.includes('/start/') && /^\/?standalone-exams\/?$/.test(url.replace(/\?.*$/, '').trim())) ||
          (url.includes('/mentors/') && url.endsWith('/') && !url.includes('/purchases')) ||
          url.includes('/course-owners/')
        );
        // Don't send auth token for auth endpoints that work without it (avoids "Invalid token" when token is stale/missing)
        const isAuthEndpointNoToken =
          url.includes('auth/login') ||
          url.includes('auth/create-account') ||
          url.includes('auth/check-signup-code');

        if (!isPublicGet && !isAuthEndpointNoToken) {
          const token = localStorage.getItem('authToken');
          if (token) {
            config.headers.Authorization = `Token ${token}`;
          }
        }
        return config;
      },
      (error) => {
        return Promise.reject(error);
      }
    );

    this.api.interceptors.response.use(
      (response) => response,
      (error) => {
        if (error.response?.status === 401) {
          const url = error.config?.url ?? '';
          const isAuthFormRequest = url.includes('auth/login') || url.includes('auth/create-account');
          const isPublicCertificateVerify = url.includes('/certificates/verify/');
          // Certificate status polling: let ExamResult show an error instead of redirect/clearing token in a loop
          const isCertificateStatusPoll =
            url.includes('/certificate/') &&
            (url.includes('/exam-attempts/') || url.includes('/standalone-exam-attempts/'));
          if (isAuthFormRequest || isPublicCertificateVerify || isCertificateStatusPoll) {
            return Promise.reject(error);
          }
          localStorage.removeItem('authToken');
          localStorage.removeItem('user');
          window.location.href = '/login';
        }
        return Promise.reject(error);
      }
    );
  }

  async login(credentials: LoginCredentials): Promise<{ token: string; user: User }> {
    const response = await this.api.post('/auth/login/', credentials);
    return { token: response.data.token, user: response.data.user };
  }

  async logout(): Promise<void> {
    await this.api.post('/auth/logout/');
  }

  async createAccount(accountData: { email: string; password: string; unique_code: string }): Promise<{ token: string; user: User }> {
    const response = await this.api.post('/auth/create-account/', accountData);
    return { token: response.data.token, user: response.data.user };
  }

  async checkSignupCode(code: string): Promise<{ valid: boolean; used: boolean; email?: string }> {
    const response = await this.api.get('/auth/check-signup-code/', { params: { code } });
    return {
      valid: response.data.valid,
      used: response.data.used,
      email: response.data.email,
    };
  }

  // Categories
  async getCategories(): Promise<Category[]> {
    const response: AxiosResponse<ApiResponse<Category>> = await this.api.get('/categories/');
    return response.data.results;
  }

  async getMentors(): Promise<Mentor[]> {
    const response: AxiosResponse<ApiResponse<Mentor>> = await this.api.get('/mentors/');
    return response.data.results;
  }

  async getFeaturedMentors(): Promise<Mentor[]> {
    const response: AxiosResponse<Mentor[]> = await this.api.get('/mentors/featured/');
    return response.data;
  }

  async getCourseOwners(): Promise<CourseOwner[]> {
    const response: AxiosResponse<ApiResponse<CourseOwner>> = await this.api.get('/course-owners/');
    return response.data.results;
  }

  async getPublishedMentors(
    page = 1,
    pageSize = 12
  ): Promise<PublishedPaginatedResponse<Mentor>> {
    const response: AxiosResponse<PublishedPaginatedResponse<Mentor>> = await this.api.get(
      '/mentors/published/',
      { params: { page, page_size: pageSize } }
    );
    return response.data;
  }

  async getCourses(): Promise<Course[]> {
    const response: AxiosResponse<ApiResponse<Course>> = await this.api.get('/courses/');
    return response.data.results;
  }

  async getPublishedCourses(
    page = 1,
    pageSize = 12,
    filters?: { search?: string; category?: number | '' }
  ): Promise<PublishedPaginatedResponse<Course>> {
    const params: Record<string, string | number> = { page, page_size: pageSize };
    if (filters?.search) params.search = filters.search;
    if (filters?.category !== undefined && filters.category !== '') params.category = filters.category as number;
    const response: AxiosResponse<PublishedPaginatedResponse<Course>> = await this.api.get(
      '/courses/published/',
      { params }
    );
    return response.data;
  }

  async getFeaturedCourses(): Promise<Course[]> {
    const response: AxiosResponse<Course[]> = await this.api.get('/courses/featured/');
    return response.data;
  }

  async getCourse(id: string): Promise<Course> {
    const response: AxiosResponse<Course> = await this.api.get(`/courses/${id}/`);
    return response.data;
  }

  async getLessons(): Promise<Lesson[]> {
    const response: AxiosResponse<ApiResponse<Lesson>> = await this.api.get('/lessons/');
    return response.data.results;
  }

  async getLessonsByCourse(courseId: number): Promise<Lesson[]> {
    const response: AxiosResponse<Lesson[]> = await this.api.get(`/lessons/by_course/?course_id=${courseId}`);
    return response.data;
  }

  async createPurchase(purchaseData: PurchaseFormData): Promise<Purchase> {
    const formData = new FormData();
    formData.append('email', purchaseData.email);
    formData.append('complete_name', purchaseData.complete_name ?? '');
    formData.append('course_id', purchaseData.course_id.toString());
    formData.append('payment_method', purchaseData.payment_method);
    formData.append('payment_proof', purchaseData.payment_proof);
    
    // Add subscription_months if provided (only for monthly subscriptions)
    if (purchaseData.subscription_months !== undefined && purchaseData.subscription_months !== null) {
      formData.append('subscription_months', purchaseData.subscription_months.toString());
    }

    const response: AxiosResponse<Purchase> = await this.api.post('/purchases/', formData, {
      headers: {
        'Content-Type': 'multipart/form-data',
      },
    });
    return response.data;
  }

  async getMyPurchases(): Promise<Purchase[]> {
    const response: AxiosResponse<Purchase[]> = await this.api.get('/purchases/my_purchases/');
    return response.data;
  }

  async getMyUnlocks(): Promise<Unlocks[]> {
    const response: AxiosResponse<Unlocks[]> = await this.api.get('/unlocks/my_unlocks/');
    return response.data;
  }

  async getDashboardStats(): Promise<Statistics> {
    const response: AxiosResponse<Statistics> = await this.api.get('/statistics/public/');
    return response.data;
  }

  // Lesson Progress
  async getLessonProgress(): Promise<LessonProgress[]> {
    const response: AxiosResponse<ApiResponse<LessonProgress>> = await this.api.get('/lesson-progress/');
    return response.data.results;
  }

  async getMyProgress(): Promise<LessonProgress[]> {
    const response: AxiosResponse<LessonProgress[]> = await this.api.get('/lesson-progress/my_progress/');
    return response.data;
  }

  async getCourseProgress(courseId: number): Promise<CourseProgress> {
    const response: AxiosResponse<CourseProgress> = await this.api.get(`/lesson-progress/course_progress/?course_id=${courseId}`);
    return response.data;
  }

  async createOrUpdateProgress(lessonId: number, progressData: { progress_percentage: number; is_completed?: boolean }): Promise<LessonProgress> {
    // First try to get existing progress
    try {
      const existing = await this.api.get(`/lesson-progress/?lesson=${lessonId}`);
      if (existing.data.results && existing.data.results.length > 0) {
        const progressId = existing.data.results[0].id;
        // Update existing progress
        const response: AxiosResponse<LessonProgress> = await this.api.patch(`/lesson-progress/${progressId}/`, progressData);
        return response.data;
      }
    } catch (error) {
      // If not found, create new
    }
    
    // Create new progress
    const response: AxiosResponse<LessonProgress> = await this.api.post('/lesson-progress/', {
      lesson_id: lessonId,
      ...progressData
    });
    return response.data;
  }

  async markLessonCompleted(progressId: number): Promise<LessonProgress> {
    const response: AxiosResponse<LessonProgress> = await this.api.post(`/lesson-progress/${progressId}/mark_completed/`);
    return response.data;
  }

  async updateProgress(progressId: number, progressPercentage: number): Promise<LessonProgress> {
    const response: AxiosResponse<LessonProgress> = await this.api.post(`/lesson-progress/${progressId}/update_progress/`, {
      progress_percentage: progressPercentage
    });
    return response.data;
  }

  async getContactInfo(): Promise<ContactInfo[]> {
    const response: AxiosResponse<ApiResponse<ContactInfo>> = await this.api.get('/contact-info/');
    return response.data.results;
  }

  async getProfile(): Promise<Profile[]> {
    const response: AxiosResponse<ApiResponse<Profile>> = await this.api.get('/profiles/');
    return response.data.results;
  }

  async getPayMethodDetails(): Promise<PayMethodDetail[]> {
    const response: AxiosResponse<ApiResponse<PayMethodDetail>> = await this.api.get('/payment-method-details/');
    return response.data.results;
  }

  // Exams
  async getExamsByCourse(courseId: number): Promise<Exam[]> {
    const response: AxiosResponse<Exam[]> = await this.api.get(`/exams/by_course/?course_id=${courseId}`);
    return response.data;
  }

  async startExam(examId: string): Promise<{ attempt: ExamAttempt; questions: Question[] }> {
    const response: AxiosResponse<{ attempt: ExamAttempt; questions: Question[] }> = await this.api.get(`/exams/${examId}/start/`);
    return response.data;
  }

  async submitAnswer(attemptId: number, questionId: number, choiceId: number): Promise<UserAnswer> {
    const response: AxiosResponse<UserAnswer> = await this.api.post(`/exam-attempts/${attemptId}/submit_answer/`, {
      question_id: questionId,
      choice_id: choiceId  // Changed from selected_choice_id to choice_id
    });
    return response.data;
  }

  async submitExam(attemptId: number): Promise<ExamAttempt> {
    const response: AxiosResponse<ExamAttempt> = await this.api.post(`/exam-attempts/${attemptId}/submit/`);
    return response.data;
  }

  async getExamAttempt(attemptId: number): Promise<ExamAttempt> {
    const response: AxiosResponse<ExamAttempt> = await this.api.get(`/exam-attempts/${attemptId}/`);
    return response.data;
  }

  async getMyExamAttempts(): Promise<ExamAttempt[]> {
    try {
      const response = await this.api.get('/exam-attempts/my_attempts/');
      
      // Check if response has results array (paginated)
      if (response.data && Array.isArray(response.data.results)) {
        return response.data.results;
      }
      
      // Check if response.data itself is an array (non-paginated)
      if (Array.isArray(response.data)) {
        return response.data;
      }
      
      // Fallback: return empty array
      console.error('Unexpected response structure for getMyExamAttempts:', response.data);
      return [];
    } catch (error) {
      console.error('Error fetching my exam attempts:', error);
      return [];
    }
  }

  // Standalone Exams (direct exam, no course)
  async getStandaloneExams(): Promise<StandaloneExam[]> {
    const response = await this.api.get('/standalone-exams/');
    return Array.isArray(response.data) ? response.data : response.data.results ?? [];
  }

  async getStandaloneExam(hashed: string): Promise<StandaloneExam> {
    const response = await this.api.get(`/standalone-exams/${hashed}/`);
    return response.data;
  }

  async startStandaloneExam(hashed: string): Promise<{
    attempt: StandaloneExamAttempt;
    questions: StandaloneQuestion[];
  }> {
    const response = await this.api.get(`/standalone-exams/${hashed}/start/`);
    return response.data;
  }

  async submitStandaloneAnswer(
    attemptId: number,
    questionId: number,
    choiceId: number
  ): Promise<unknown> {
    const response = await this.api.post(
      `/standalone-exam-attempts/${attemptId}/submit_answer/`,
      { question_id: questionId, choice_id: choiceId }
    );
    return response.data;
  }

  async submitStandaloneExam(attemptId: number): Promise<StandaloneExamResultSummary> {
    const response = await this.api.post(`/standalone-exam-attempts/${attemptId}/submit/`);
    return response.data;
  }

  async getStandaloneExamAttempt(attemptId: number): Promise<StandaloneExamAttempt> {
    const response = await this.api.get(`/standalone-exam-attempts/${attemptId}/`);
    return response.data;
  }

  async getMyStandaloneExamAttemptsForExam(examId: number): Promise<StandaloneExamAttempt[]> {
    const response = await this.api.get('/standalone-exam-attempts/', {
      params: { standalone_exam: examId },
    });
    const data = response.data;
    if (Array.isArray(data)) {
      return data;
    }
    if (Array.isArray(data.results)) {
      return data.results;
    }
    return [];
  }

  async createStandaloneExamPurchase(data: StandaloneExamPurchaseFormData): Promise<StandaloneExamPurchase> {
    const formData = new FormData();
    formData.append('standalone_exam_id', data.standalone_exam_id.toString());
    formData.append('email', data.email);
    formData.append('complete_name', data.complete_name ?? '');
    formData.append('payment_method', data.payment_method);
    formData.append('payment_proof', data.payment_proof);
    if (data.whatsapp_number != null && data.whatsapp_number !== '') {
      formData.append('whatsapp_number', data.whatsapp_number);
    }
    if (data.subscription_months != null && data.subscription_months !== undefined) {
      formData.append('subscription_months', data.subscription_months.toString());
    }
    const response = await this.api.post('/standalone-exam-purchases/', formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
    });
    return response.data;
  }

  async getMyStandaloneExamPurchases(): Promise<StandaloneExamPurchase[]> {
    const response = await this.api.get('/standalone-exam-purchases/my_standalone_purchases/');
    return response.data;
  }

  /** Poll exam-attempt certificate generation status (requires Token auth — use axios, not raw fetch). */
  async getExamAttemptCertificateStatus(attemptId: number): Promise<{
    status: string;
    certificate_url?: string;
    certificate_preview_url?: string;
    error_message?: string;
  }> {
    const response = await this.api.get(`/exam-attempts/${attemptId}/certificate/`);
    return response.data;
  }

  /** Poll standalone exam certificate generation status. */
  async getStandaloneExamAttemptCertificateStatus(attemptId: number): Promise<{
    status: string;
    certificate_url?: string;
    certificate_preview_url?: string;
    error_message?: string;
  }> {
    const response = await this.api.get(`/standalone-exam-attempts/${attemptId}/certificate/`);
    return response.data;
  }

  // Certificates
  async getCertificateVerify(token: string): Promise<CertificateVerifyResult> {
    const response: AxiosResponse<CertificateVerifyResult> = await this.api.get(
      `/certificates/verify/${encodeURIComponent(token)}/`
    );
    return response.data;
  }

  async downloadCertificateByToken(token: string, filename: string): Promise<void> {
    const response = await this.api.get(`/certificates/verify/${encodeURIComponent(token)}/download/`, {
      responseType: 'blob',
    });
    const blob = new Blob([response.data], { type: 'application/pdf' });
    const url = window.URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = filename;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    window.URL.revokeObjectURL(url);
  }

  async getCertificatePreviewCourse(attemptId: number): Promise<{ image_url?: string; html?: string }> {
    const response: AxiosResponse<{ image_url?: string; html?: string }> = await this.api.get(
      `/certificates/course/${attemptId}/preview/`
    );
    return response.data;
  }

  async downloadCertificateCourse(attemptId: number, filename: string): Promise<void> {
    const response = await this.api.get(`/certificates/course/${attemptId}/download/`, {
      responseType: 'blob',
    });
    const blob = new Blob([response.data], { type: 'application/pdf' });
    const url = window.URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = filename;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    window.URL.revokeObjectURL(url);
  }

  async getCertificatePreviewStandalone(attemptId: number): Promise<{ image_url?: string; html?: string }> {
    const response: AxiosResponse<{ image_url?: string; html?: string }> = await this.api.get(
      `/certificates/standalone/${attemptId}/preview/`
    );
    return response.data;
  }

  async downloadCertificateStandalone(attemptId: number, filename: string): Promise<void> {
    const response = await this.api.get(`/certificates/standalone/${attemptId}/download/`, {
      responseType: 'blob',
    });
    const blob = new Blob([response.data], { type: 'application/pdf' });
    const url = window.URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = filename;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    window.URL.revokeObjectURL(url);
  }
}

export const apiService = new ApiService();
export default apiService;